import React, { useState } from 'react';
import { Button, Typography, Box, IconButton, Popover, Tooltip, CircularProgress } from '@mui/material';
import { CloudUpload, ContentCopy, DoneAll, Download, InsertDriveFile } from '@mui/icons-material';
import { useFileUpload } from '../../hooks/useFileUpload';
import useGlobal from '../../util/Global';

const styles = {
    container: { textAlign: 'center', width: '100%' },
    title: { mb: 1, color: 'text.secondary' },
    button: { borderRadius: 2, textTransform: 'none' },
    previewBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mt: 1,
        p: 1,
        border: '0px solid',
        borderRadius: 1,
        maxWidth: '80%',
        mx: 'auto',
        bgcolor: 'background.paper',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',  // <-- shadow added here
    },

    error: { mt: 1, color: 'error.main' },
};

/**
 * @param {{
 *  title?: string,
 *  onChange?: (file: File) => void,
 *  initialPreview?: string,
 *  allowedTypes?: string[],
 *  maxFileSizeMB?: number
 * }} props
 */
export function FileUploader({
    onChange,
    initialPreview = null,
    allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
    maxFileSizeMB = 10,
    isLoading = false,
    loadingKey = 'button.uploading',
    labelAddKey = '',
    labelUpdateKey = '',
    beforeAddMessage = '',
    afterAddMessage = ''
}) {

    const [anchorEl, setAnchorEl] = useState(null);  // For Popover anchor element
    const [copied, setCopied] = useState(false);     // For showing copy feedback


    const { t } = useGlobal();
    const validator = (file) => {
        if (!allowedTypes.includes(file.type)) return 'Invalid file type.';
        if (file.size > maxFileSizeMB * 1024 * 1024) return `File must be under ${maxFileSizeMB}MB.`;
        return '';
    };

    const { fileName, error, inputRef, handleFileChange } = useFileUpload({
        initialPreview,
        validator: validator,
        onFileChange: onChange,

    });

    const accept = allowedTypes.map(type => {
        switch (type) {
            case 'application/pdf': return '.pdf';
            case 'application/msword': return '.doc';
            case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': return '.docx';
            case 'application/vnd.ms-excel': return '.xls';
            case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': return '.xlsx';
            default: return type;
        }
    });

    const handleIconClick = (event) => {
        // Toggle popover open/close by setting anchorEl
        setAnchorEl(anchorEl ? null : event.currentTarget);
        setCopied(false);  // Reset copy feedback when toggling popover
    };

    const handleCopy = () => {
        if (!fileName) return;
        navigator.clipboard.writeText(fileName).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };

    const popOver = (
        <Popover
            id="file-name-popover"
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
                <Typography noWrap sx={{ flexGrow: 1 }}>
                    {fileName}
                </Typography>
                <Tooltip title={copied ? "Copied!" : "Copy filename"}>
                    <IconButton size="small" onClick={handleCopy}>
                        <ContentCopy fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Popover>
    );

    const getLabel = () => {
        if (isLoading)
            return t(loadingKey);

        if (fileName)
            return t(labelUpdateKey)

        return t(labelAddKey)
    }

    const iconUpload = () => {
        if (isLoading)
            return <CircularProgress size={24} sx={{ color: 'white' }} />;

        if (fileName)
            return <DoneAll />

        return <CloudUpload />
    }

    return (
        <Box sx={styles.container}>

            <input
                type="file"
                ref={inputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                accept={accept.join(',')}
            />
            <Button
                variant="contained"
                onClick={() => inputRef.current?.click()}
                sx={styles.button}
                startIcon={iconUpload()}
            >
                {getLabel()}
            </Button>

            {!fileName && !isLoading && <Box sx={styles.previewBox}>
                <Typography variant="overline" sx={{ mt: 1 }}>
                    {t(beforeAddMessage)}
                </Typography>
            </Box>}

            {fileName && !isLoading && (
                <Box sx={styles.previewBox}>
                    <InsertDriveFile
                        sx={{ mr: 1, cursor: 'pointer' }}
                        onClick={handleIconClick}
                        aria-describedby="file-name-popover"
                    />
                    {popOver}
                    <Typography variant="overline" sx={{ mt: 1 }}>
                        {fileName ? t(afterAddMessage) : t(beforeAddMessage)}

                    </Typography>
                    {fileName && (
                        <IconButton
                            variant="outlined"
                            color="primary"
                            sx={{ mt: 1 }}
                            onClick={() => window.open(fileName, '_blank')}
                        >
                            <Download />
                        </IconButton>)}
                </Box>
            )}
            {error && <Typography variant="body2" sx={styles.error}>{error}</Typography>}
        </Box>
    );
}
